import React, { useState, useEffect, useRef, useCallback } from 'react';
import CollapsibleList from './CollapsibleList';
import CollapsibleListView from './CollapsibleListView';
import 'bootstrap/dist/css/bootstrap.min.css';
import { fetchSelectedIds, saveSelectedIds } from '../../../api/frameworks'; // Importa las funciones de API actualizadas
import ListItem from './ListItem';


// --- Funciones Auxiliares para Manipulación del Árbol ---

// Función para actualizar el estado de checked de un nodo
// Si se marca, los hijos se expanden (visible = true)
// Si se desmarca, la visibilidad no cambia.
const updateCheckedState = (tree, idToToggle, isChecked) => {
  return tree.map((node) => {
    if (node.id === idToToggle) {
      // Si se marca, se establece visible en true. Si se desmarca, se mantiene el visible actual.
      const newVisibleState = isChecked ? true : node.visible;
      return { ...node, checked: isChecked, visible: newVisibleState };
    }
    if (node.children && node.children.length > 0) {
      return { ...node, children: updateCheckedState(node.children, idToToggle, isChecked) };
    }
    return node;
  });
};

// Función para alternar el estado de visible de un nodo (solo en memoria)
const toggleVisibilityState = (tree, idToToggle) => {
  return tree.map((node) => {
    if (node.id === idToToggle) {
      return { ...node, visible: !node.visible };
    }
    if (node.children && node.children.length > 0) {
      return { ...node, children: toggleVisibilityState(node.children, idToToggle) };
    }
    return node;
  });
};

// Función para extraer solo los IDs de los ítems con checked: true
const getAllCheckedIds = (tree) => {
  let checkedIds = [];
  tree.forEach(node => {
    if (node.checked) {
      checkedIds.push({ id: node.id, parent_id: node.parent_id, checked: true }); // Incluye parentId si es necesario
    }
    if (node.children && node.children.length > 0) {
      checkedIds = checkedIds.concat(getAllCheckedIds(node.children));
    }
  });
  return checkedIds;
};

// --- FUNCIÓN CLAVE MODIFICADA ---
// Función para "hidratar" el árbol con los IDs seleccionados desde la DB
// y asegurar que los padres sean visibles si un hijo está chequeado.
const hydrateTreeWithSelections = (tree, selectedIdsFromDB) => {
  // Primero, marcamos los elementos checked y establecemos su propia visibilidad
  const markedTree = tree.map(node => {
    const isChecked = selectedIdsFromDB.includes(node?.id);
    // Un nodo es visible al inicio si está chequeado (y tiene hijos)
    // o si algún hijo está chequeado (esto se manejará en la segunda pasada)
    const newVisible = isChecked && node?.children && node.children?.length > 0;

    const updatedNode = { ...node, checked: isChecked, visible: newVisible };

    if (node?.children && node?.children.length > 0) {
      updatedNode.children = hydrateTreeWithSelections(node?.children, selectedIdsFromDB);
    }
    return updatedNode;
  });

  // Segunda pasada: Propagar la visibilidad hacia arriba
  // Si un hijo está checked, su padre debe ser visible.
  const propagateVisibilityUp = (nodes) => {
    return nodes.map(node => {
      let updatedNode = { ...node };

      if (updatedNode.children && updatedNode.children.length > 0) {
        // Primero, procesa los hijos recursivamente
        updatedNode.children = propagateVisibilityUp(updatedNode.children);

        // Si alguno de los hijos es visible (porque está chequeado o por otra razón),
        // o si el propio nodo está chequeado, entonces el nodo actual debe ser visible.
        const anyChildVisible = updatedNode.children.some(child => child.visible || child.checked);
        if (anyChildVisible || updatedNode.checked) {
          updatedNode.visible = true;
        }
      }
      return updatedNode;
    });
  };

  return propagateVisibilityUp(markedTree);
};

function InteractivePanel({ propsData, close, save, saveNote, getNote, download, type, format = 'framework', setIsNoteActive, totalItemsSelected = 0, limitPerFramework = 0, subscription = "", itemsSelected, projectId }) {

  const [path, setPath] = useState([]);
  const [data, setData] = useState();
  const [loading, setLoading] = useState(true);
  const [currentNoteId, setCurrentNoteId] = useState("");
  const [currentNoteName, setCurrentNoteName] = useState("");
  const [currentNoteMobileValue, setCurrentNoteMobileValue] = useState("");
  const [totalChecked, setTotalChecked] = useState(0);
  const [treeNotSaved, setTreeNotSaved] = useState(false);

  const [seconds, setSeconds] = useState(0); // Contador de segundos
  const [lastCompletedTime, setLastCompletedTime] = useState(null); // Última vez que se cumplió 60 segundos

  useEffect(() => {
    const interval = setInterval(() => {
      setSeconds((prev) => {
        if (prev === 3599) {
          const now = new Date();
          setLastCompletedTime(now); // Guardar la hora actual cuando se cumplen los 60 segundos
          handleAutoSave();
          return 0; // Reiniciar contador
        }
        return prev + 1;
      });
    }, 1000); // Incrementar cada segundo

    return () => clearInterval(interval); // Limpiar intervalo al desmontar
  }, []);

  // Calcular hace cuántos minutos fue la última vez
  const timeAgo = lastCompletedTime
    ? Math.floor((new Date().getTime() - lastCompletedTime.getTime()) / 60000)
    : null;

  useEffect(() => {
    const loadSelections = async () => {
      setLoading(true);
      const selectedIdsStore = await fetchSelectedIds(propsData.id, projectId);
      // Hidrata el árbol con los IDs chequeados, y establece 'visible' según esa condición
      let hydratedData = [];
      if (selectedIdsStore.length && (selectedIdsStore.length != itemsSelected.length)) {
        const selectedIds = selectedIdsStore.map(item => item.id); // Asumiendo que itemsSelected es un array de objetos con un campo 'id'
        hydratedData = hydrateTreeWithSelections([propsData], selectedIds);
        setTreeNotSaved(true);
      } else {
        const selectedIds = itemsSelected.map(item => item.id); // Asumiendo que itemsSelected es un array de objetos con un campo 'id'
        hydratedData = hydrateTreeWithSelections([propsData], selectedIds);
      }

      setData(hydratedData);
      setLoading(false);
    };
    if (format === 'framework') {
      loadSelections();
    } else if (format === 'wbs') {
      setData([propsData]);
      setLoading(false);
    } else {
      setData([]);
      setLoading(false);
    }
  }, [propsData]);

  useEffect(() => {
    if (type === 'edit') {
      let calculatePercent = (totalChecked * 100) / limitPerFramework;
      if (calculatePercent > 90) {
        close();
      }
    }
  }, [totalChecked])

  useEffect(() => {
    setTotalChecked(totalItemsSelected);
  }, [totalItemsSelected])

  /**
 * Update 21-06-2025
 * This function fetches the selected IDs from localstorage
 */
  const handleSave = async () => {
    const selectedIds = await fetchSelectedIds(propsData.id, projectId);
    save(selectedIds, false);
  };

  const handleAutoSave = async (props = {}) => {

    const selectedIds = await fetchSelectedIds(propsData.id, projectId);
    save(selectedIds, false);
  };

  const handleSaveNote = (currentNoteId, note) => {
    //handleAutoSave({ currentNoteId: currentNoteId, note: note });
    saveNote(currentNoteId, note);
  }
  const handleGetNote = async (currentNoteId) => {
    let note = await getNote(currentNoteId);
    setCurrentNoteMobileValue(note)

  }
  const handleChangeNoteMobile = (e) => {
    setCurrentNoteMobileValue(e.target.value);
  }

  //---- new changes ----

  // Manejador para el cambio de checkbox
  const handleToggleChecked = useCallback(async (id, isChecked) => {
    setData((prevTreeData) => {
      const newTreeData = updateCheckedState(prevTreeData, id, isChecked);
      // Extrae solo los IDs de los ítems marcados para guardar
      const currentCheckedIds = getAllCheckedIds(newTreeData);

      saveSelectedIds(currentCheckedIds, propsData.id, projectId); // Guarda en la "DB"
      return newTreeData;
    });
  }, []);

  // Manejador para el click en el nombre (toggle de visibilidad)
  // ESTA FUNCIÓN NO GUARDA EL ESTADO DE VISIBILIDAD EN LA DB.
  const handleToggleVisible = useCallback((id) => {
    setData((prevTreeData) => {
      const newTreeData = toggleVisibilityState(prevTreeData, id);
      // No se llama a saveSelectedIds aquí para la visibilidad
      return newTreeData;
    });
  }, []);

  const handleShowNoteCanvas = (id = "", name = "") => {
    setCurrentNoteId(id);
    setCurrentNoteName(name);

    /**
     * Buscar optimizar para que no se haga una llamada a la API cada vez que se presione en nota despues de guardar
     */
    handleGetNote(id);
    // let findNote = itemsSelected.find(item => item.id === id);

    // if (findNote) {
    //   setCurrentNoteMobileValue(findNote.note || "");
    // }



  }
  const handleRemoveChangesNotSaved = () => {
    localStorage.removeItem(`selected_ids_${projectId}_${propsData.id}`);
    localStorage.removeItem(`save_selection_${projectId}_${propsData.id}`);
    setTreeNotSaved(false);
    close();
  }

  const DesktopButtons = (
    <div className={'d-none d-md-flex justify-content-end w-100 align-items-center px-4 container-button-group'}>

      
      {type === 'edit' &&
        <button className={'btn btn-primary mr-2'} onClick={handleSave}>Save</button>
      }
      {(type === 'edit' && treeNotSaved) &&
        <button className={'btn btn-warning mr-2'} onClick={handleRemoveChangesNotSaved}>Discard Changes</button>
      }

     
      {(type === 'show' && format === 'framework' && subscription === 'Basic') &&
        <button className="btn btn-primary mr-2" onClick={() => { download(data.id) }}>Download</button>
      }
      {(type === 'show' && format === 'wbs') &&
        <button className="btn btn-primary mr-2" onClick={() => { download(data.id) }}>Download</button>
      }

     
      <button className={'btn btn-secondary'} onClick={close}>Close</button>
    </div>
  );

  
  const MobileButtons = (
    <div className={'d-flex d-md-none justify-content-center w-100 align-items-center px-0 container-button-group'}>

      
      {type === 'edit' &&
        <>
          <button className={'btn btn-primary mr-2'} style={{ width: '50%' }} onClick={handleSave}>Save</button>
          {/* Ocultamos Discard en móvil, o lo movemos a un menú, o lo mostramos solo si es crucial */}
        </>
      }

      
      {(type === 'show' && (format === 'framework' || format === 'wbs') && subscription === 'Basic') &&
        <button className="btn btn-primary mr-2" style={{ width: '50%' }} onClick={() => { download(data.id) }}>
          {format === 'framework' ? 'Download Framework' : 'Download Table of Content'}
        </button>
      }

      
      <button className={'btn btn-secondary'} style={ {width:'50%'} } onClick={close}>Close</button>
    </div>
  );


  return (
    <div className='cont-modal-framework' >
      {/* OffCanva for notes */}
      <div className={`overlay-offCanvas ${currentNoteId ? 'active' : ''}`}>
        <div className={`offCanvas ${currentNoteId ? 'active' : 'hidden'}`}>
          <div className='d-flex flex-row justify-content-end'>
            <small style={{ color: '#b91e1e', cursor: 'pointer' }} onClick={() => { setCurrentNoteId("") }}>Close X</small>
          </div>
          <h6 className='mt-2'>Add note for {currentNoteName}</h6>
          <textarea className='mt-2' value={currentNoteMobileValue} onChange={(e) => { handleChangeNoteMobile(e) }}></textarea>
          <div className='d-flex flex-column flex-md-column justify-content-center justify-content-md-start container-button-group mt-3'>
            <span className='mb-2' style={{ color: '#1a67a3', cursor: 'pointer' }} onClick={() => { handleSaveNote(currentNoteId, currentNoteMobileValue) }}><i class="bi bi-floppy2-fill" ></i> Save note</span>
            <span style={{ color: '#1a67a3', cursor: 'pointer' }} onClick={() => { handleSaveNote(currentNoteId, currentNoteMobileValue); setCurrentNoteId("") }}><i class="bi bi-floppy2-fill" ></i> Save note and exit</span>
            {/* <button className='btn btn-primary' onClick={() => { handleSaveNote(currentNoteId, currentNoteMobileValue) }}>Save Note</button>
            <button className='btn btn-primary mx-0 mx-md-2' onClick={() => { handleSaveNote(currentNoteId, currentNoteMobileValue); setCurrentNoteId("") }}>Save Note And Exit</button> */}
          </div>
        </div>
      </div>

      <div className='d-flex flex-column flex-md-row justify-content-between p-2 cont-button-group bg-light'>
        {lastCompletedTime &&
          <div className='d-flex flex-row justify-content-start w-100 align-items-center cont-autosave'>
            <i class="bi bi-floppy-fill" style={{ fontSize: '14px', color: 'gray' }}> </i>
            <span className="d-flex flex-row justify-content-start w-100 align-items-center" style={{ fontSize: '14px', margin: '0px', color: 'gray', fontStyle: 'italic' }}>&nbsp;&nbsp;Auto Save - {timeAgo} min ago</span>
          </div>
        }

        <>
          {DesktopButtons}
          {MobileButtons}
        </>

      </div>
      <div className={" body-framework-items "}>
        {data && type === 'edit' &&
          data[0].children.map((item) => (
            <ListItem
              key={item.id}
              item={item}
              onToggleChecked={handleToggleChecked}
              onToggleVisible={handleToggleVisible}
              onShowNoteCanvas={handleShowNoteCanvas}
            />
          ))
        }
        {data && type === 'show' && <CollapsibleListView items={data[0].children ?? []} padding={0} format={format} />}
      </div>


    </div>
  );
}

export default InteractivePanel;
